import * as React from 'react';
import { LeftOutlined, RightOutlined, VerticalRightOutlined } from '@ant-design/icons';
import { Tooltip, InputNumber } from 'src/design-system/components/atoms';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useTheme } from 'src/theming/useTheme';

const useStyles = createUseStyles((theme: ThemeType) => ({
  disabledStyle: {
    pointerEvents: 'none',
    cursor: 'default',
    color: theme.color.semantic.text.subtle,
  },
  pageNumberInput: {
    width: '45px',
    lineHeight: theme.typography.lineHeight.none,
    '& .ant-input-number-handler-wrap': {
      display: 'none',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing[1],
    cursor: 'pointer',
    direction: 'ltr',
    '& div': {
      color: theme.color.semantic.brand.default.primary,
    },
  },
}));

interface SimplePaginatorProps extends WithTranslation {
  pageNumber: number;
  isNext: boolean;
  loading?: boolean;
  onFirstClick: () => void;
  onPrevClick: () => void;
  onNextClick: () => void;
  onPageJumped?: (pageNumber: number) => void;
}

interface SimplePaginatorState {
  localPageNumber: number;
}

class SimplePaginatorClass extends React.PureComponent<SimplePaginatorProps, SimplePaginatorState> {
  state: SimplePaginatorState = {
    localPageNumber: 1,
  };

  onPageNumberChange = (localPageNumber: number | null) => {
    if (localPageNumber !== null) {
      this.setState({ localPageNumber });
    }
  };

  onPageNumberStep = (localPageNumber: number | null) => {
    if (localPageNumber !== null) {
      this.setState({ localPageNumber }, this.applyPageNumber);
    }
  };

  applyPageNumber = () => {
    if (this.props.onPageJumped) {
      this.props.onPageJumped(this.state.localPageNumber);
    }
  };

  renderPageNumberInput = () => {
    const { pageNumber, isNext, loading } = this.props;
    return (
      <SimplePaginatorInput
        pageNumber={pageNumber}
        isNext={isNext}
        loading={loading}
        onPageNumberChange={this.onPageNumberChange}
        onPageNumberStep={this.onPageNumberStep}
        applyPageNumber={this.applyPageNumber}
      />
    );
  };

  render() {
    const { pageNumber, isNext, loading, t, onPageJumped } = this.props;
    return (
      <SimplePaginatorInner
        pageNumber={pageNumber}
        isNext={isNext}
        loading={loading}
        t={t}
        onFirstClick={this.props.onFirstClick}
        onPrevClick={this.props.onPrevClick}
        onNextClick={this.props.onNextClick}
        renderPageNumberInput={onPageJumped ? this.renderPageNumberInput : null}
      />
    );
  }
}

interface SimplePaginatorInputProps {
  pageNumber: number;
  isNext: boolean;
  loading?: boolean;
  onPageNumberChange: (pageNumber: number | null) => void;
  onPageNumberStep: (pageNumber: number | null) => void;
  applyPageNumber: () => void;
}

const SimplePaginatorInput: React.FC<SimplePaginatorInputProps> = ({
  pageNumber,
  isNext,
  loading,
  onPageNumberChange,
  onPageNumberStep,
  applyPageNumber,
}) => {
  const classes = useStyles();
  return (
    <InputNumber
      className={classes.pageNumberInput}
      disabled={loading}
      size="middle"
      min={1}
      step={1}
      max={isNext ? pageNumber + 1 : pageNumber}
      value={pageNumber}
      onChange={loading ? undefined : onPageNumberChange}
      onStep={loading ? undefined : onPageNumberStep}
      onPressEnter={loading ? undefined : applyPageNumber}
    />
  );
};

interface SimplePaginatorInnerProps {
  pageNumber: number;
  isNext: boolean;
  loading?: boolean;
  t: (key: string) => string;
  onFirstClick: () => void;
  onPrevClick: () => void;
  onNextClick: () => void;
  renderPageNumberInput: (() => React.ReactNode) | null;
}

const SimplePaginatorInner: React.FC<SimplePaginatorInnerProps> = ({
  pageNumber,
  isNext,
  loading,
  t,
  onFirstClick,
  onPrevClick,
  onNextClick,
  renderPageNumberInput,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div className={classes.container} style={{ direction: 'ltr' }}>
      <Tooltip title={t('Go to First Page')}>
        <a>
          <VerticalRightOutlined
            className={pageNumber !== 1 && !loading ? '' : classes.disabledStyle}
            style={{ marginRight: theme.spacing[1] }}
            onClick={pageNumber !== 1 && !loading ? onFirstClick : undefined}
          />
        </a>
      </Tooltip>
      <Tooltip title={t('Go to Previous Page')}>
        <a>
          <LeftOutlined
            className={pageNumber !== 1 && !loading ? '' : classes.disabledStyle}
            onClick={pageNumber !== 1 && !loading ? onPrevClick : undefined}
          />
        </a>
      </Tooltip>
      <div style={{ margin: `${theme.spacing[0]} ${theme.spacing[1]}` }}>
        {renderPageNumberInput ? renderPageNumberInput() : pageNumber}
      </div>
      <Tooltip title={t('Go to Next Page')}>
        <a>
          <RightOutlined
            className={isNext && !loading ? '' : classes.disabledStyle}
            onClick={isNext && !loading ? onNextClick : undefined}
          />
        </a>
      </Tooltip>
    </div>
  );
};

const SimplePaginator = withTranslation()(SimplePaginatorClass);
export default SimplePaginator;
